import axios from 'axios';
import React from 'react';

interface VerbaScholaResource<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    reload: () => void;
}

/**
 * Load one VerbaSchola endpoint into loading/error/data state.
 *
 * The teacher API answers 401 for a course that exists but is not yours and 404 when it does
 * not exist at all, so those are surfaced as distinct messages rather than a generic failure.
 */
export default function useVerbaScholaResource<T>(
    request: () => Promise<{ data: T }>,
    dependencyList: React.DependencyList,
    notFoundMessage = 'Not found.',
): VerbaScholaResource<T> {
    const [data, setData] = React.useState<T | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [reloadCount, setReloadCount] = React.useState(0);
    const reload = React.useCallback(() => setReloadCount(count => count + 1), []);

    // `request` is a fresh closure on every render, so the caller's dependency list is the
    // real trigger. Including `request` here would refetch in a loop.
    const requestRef = React.useRef(request);
    requestRef.current = request;

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        requestRef.current()
            .then(response => {
                if (cancelled) return;
                setData(response.data);
            })
            .catch(caught => {
                if (cancelled) return;
                const status = axios.isAxiosError(caught) ? caught.response?.status : undefined;
                // Never leave previously loaded protected data on screen after access is
                // revoked or the resource disappears during a background refresh.
                if (status === 401 || status === 403 || status === 404) setData(null);
                if (status === 404) {
                    setError(notFoundMessage);
                } else if (status === 401 || status === 403) {
                    setError('You do not have access to this.');
                } else {
                    setError('Could not load this page. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...dependencyList, reloadCount]);

    return { data, loading, error, reload };
}
